/**
 * Auth Redux Slice for MVP
 * Manages authentication state and user session
 */
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { loginRequest, LoginRequest, checkExecutiveAccess } from '../../../services/authService';

import { SecureStorage } from '../../../utils/SecureStorage';
export interface Organization {
    organization_uuid: string;
    organization_name: string;
    dba_name?: string;
    role?: string;
    is_organization_admin?: boolean;
    is_organization_superadmin?: boolean;
    is_tg_superadmin?: boolean;
}

export interface RoleAssignment {
    org_pfuser_role_assignment_id: number;
    org_pfuser_association_uuid: string;
    organization_uuid: string;
    legal_name?: string; // DB: Legal_Name → normalize_row() → legal_name
    pfuser_uuid: string; // ✅ FIXED: pfuser_uuid (NOT pf_user_uuid)
    orgrole_id: number; // DB: OrgRole_ID → normalize_row() → orgrole_id
    // ORG.fk_Org_Role_Assignment_Status: 0=Undefined,1=Registered Backup Only,2=Active Role Assignment,3=Pending Role Termination,4=Terminated
    role_assignment_status_id?: number;
    created_date?: string;
    user_defined_role_title?: string;
    orgmgthierarchy?: string; // DB: OrgMgtHierarchy → normalize_row() → orgmgthierarchy (NO underscores)
    department?: string;
    deptgroup?: string; // DB: DeptGroup → normalize_row() → deptgroup (NO underscores)
    orgproductivity?: string; // DB: OrgProductivity → normalize_row() → orgproductivity (NO underscores)
    orgquality?: string; // DB: OrgQuality → normalize_row() → orgquality (NO underscores)
    preferred_email?: string; // DB: Preferred_Email → normalize_row() → preferred_email
}

export interface PFUser {
    pfuser_id: number;
    username: string;
    user_uuid: string;
    email?: string;
    first_name?: string;
    last_name?: string;
}

export interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    accessToken: string | null;
    pfUser: PFUser | null;
    activeOrganizationUUID: string | null;
    activeOrgRoleId: number | null;
    userOrganizations: Organization[];
    userRoleAssignments: RoleAssignment[];
    permissions: string[];
    isSuperAdmin: boolean;
    isOrgAdmin: boolean;
    hasExecutiveAccess: boolean;
    executiveAccessType?: number;
    executiveAccessName?: string;
}

const initialState: AuthState = {
    isAuthenticated: !!SecureStorage.getAccessToken(),
    isLoading: false,
    error: null,
    accessToken: SecureStorage.getAccessToken(),
    pfUser: null,
    activeOrganizationUUID: null,
    activeOrgRoleId: null,
    userOrganizations: [],
    userRoleAssignments: [],
    permissions: [],
    isSuperAdmin: false,
    isOrgAdmin: false,
    hasExecutiveAccess: false,
};

/**
 * Async thunk for login
 */
export const login = createAsyncThunk(
    'auth/login',
    async (credentials: LoginRequest, { rejectWithValue }) => {
        try {
            const response = await loginRequest(credentials);

            if (!response.success) {
                return rejectWithValue(response.detail || 'Login failed');
            }

            // Check executive access
            const execAccess = await checkExecutiveAccess(credentials.username);

            return {
                accessToken: response.access_token!,
                pfUser: {
                    pfuser_id: response.pfuser_id || 0,
                    username: response.username || credentials.username,
                    user_uuid: response.user_uuid || '',
                },
                hasExecutiveAccess: execAccess.has_access,
                executiveAccessType: execAccess.access_type,
                executiveAccessName: execAccess.access_type_name,
            };
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
        }
    },
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setAccessToken: (state, action: PayloadAction<string>) => {
            state.accessToken = action.payload;
            state.isAuthenticated = true;
        },
        setPfUser: (state, action: PayloadAction<PFUser>) => {
            state.pfUser = action.payload;
        },
        setActiveOrganizationUUID: (state, action: PayloadAction<string>) => {
            state.activeOrganizationUUID = action.payload;
        },
        setActiveOrgRoleId: (state, action: PayloadAction<number | null>) => {
            state.activeOrgRoleId = action.payload;
        },
        setUserOrganizations: (state, action: PayloadAction<Organization[]>) => {
            state.userOrganizations = action.payload;
        },
        setUserRoleAssignments: (state, action: PayloadAction<RoleAssignment[]>) => {
            state.userRoleAssignments = action.payload;
        },
        setSuperAdminStatus: (state, action: PayloadAction<boolean>) => {
            state.isSuperAdmin = action.payload;
        },
        logout: (state) => {
            state.isAuthenticated = false;
            state.accessToken = null;
            state.pfUser = null;
            state.activeOrganizationUUID = null;
            state.userOrganizations = [];
            state.userRoleAssignments = [];
            state.permissions = [];
            state.isSuperAdmin = false;
            state.hasExecutiveAccess = false;
            state.executiveAccessType = undefined;
            state.executiveAccessName = undefined;
            SecureStorage.clearAuth();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(login.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(login.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.accessToken = action.payload.accessToken;
                state.pfUser = action.payload.pfUser;
                state.hasExecutiveAccess = action.payload.hasExecutiveAccess;
                state.executiveAccessType = action.payload.executiveAccessType;
                state.executiveAccessName = action.payload.executiveAccessName;
                state.error = null;
            })
            .addCase(login.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const {
    setAccessToken,
    setPfUser,
    setActiveOrganizationUUID,
    setActiveOrgRoleId,
    setUserOrganizations,
    setUserRoleAssignments,
    setSuperAdminStatus,
    logout: logoutAction,
} = authSlice.actions;
export default authSlice.reducer;
